// The shell's side of the contents' filter (store/binderFilter.ts): the state this browser keeps for the project, the
// arrangement filtered as words are typed (150 ms after the last key), a document edited out of the filter kept while
// it's selected, and the controller the bar, the contents and the pages share through the workspace.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ArrangementInfo, OpenedProject, Project, TreeNode } from '@writing-suite/core';
import { savedSearchesOf, type SavedSearch } from '@writing-suite/core/plan';
import {
  activeFilter, addFacet, clearFilter, filterArrangement, filterBy, filteredManuscript, filterViewKey, focus, fragmentSnippet, isFilterEdited, keepSelection,
  loadFilter, readStoredFilter, removeFacet, replaceFacet, selectedSearch, snippetQuery, stepTarget, storedFilter, visibleNode, visibleTree,
  type BinderFilterState, type FilterView,
} from '../store/binderFilter.ts';
import { onFilterRequest, PREF, useSharedPreference } from '../store/plan.ts';
import { storageId } from '../store/storageId.ts';
import { announce } from './context.ts';
import type { BinderFilter } from './workspace.ts';

/** Words typed settle this long before the contents filter by them. */
const SETTLE_MS = 150;

export function useBinderFilter(o: {
  project: Project | null;
  /** The project's address (the key the filter is kept under while the project's own id is read). */
  projectKey: string;
  arrangement: ArrangementInfo | null;
  fullTree: TreeNode[];
  selected: string | null;
  locks: ReadonlySet<string>;
  /** Shows a node (null: the arrangement's top) on the page the contents open items on. */
  select: (nodeId: string | null) => void;
  /** Shows the manuscript (or Research & Notes), for a collection that looks there loaded while another arrangement shows. */
  show: (role: 'manuscript' | 'reference') => void;
}): { filter: BinderFilter | null; tree: TreeNode[]; node: TreeNode | null } {
  const { project, arrangement, fullTree, selected, locks } = o;
  const opened = project && (project as OpenedProject).record ? (project as OpenedProject) : null;
  const [raw, setRaw] = useSharedPreference<unknown>(PREF.binderFilter(project ? storageId(project.id) : o.projectKey), null);
  const state = readStoredFilter(raw);
  const set = (next: BinderFilterState) => setRaw(storedFilter(next));

  // The typed words settle before they filter; chips filter at once.
  const [settledText, setSettledText] = useState(state.text);
  useEffect(() => {
    if (state.text === settledText) return;
    if (!state.text) return setSettledText('');
    const t = setTimeout(() => setSettledText(state.text), SETTLE_MS);
    return () => clearTimeout(t);
  }, [state.text]); // eslint-disable-line react-hooks/exhaustive-deps
  const settled = useMemo(() => ({ ...state, text: settledText }), [state, settledText]);
  const active = useMemo(() => activeFilter(settled), [settled]);
  const searches = useMemo(() => (opened ? savedSearchesOf(opened.record.settings) : []), [opened?.record]); // eslint-disable-line react-hooks/exhaustive-deps
  const saved = selectedSearch(state, searches);
  const isSaved = !!saved;

  // The arrangement filtered, with the selection kept when an edit took it out.
  const stored = opened && arrangement ? opened.record.arrangements.find((a) => a.id === arrangement.id) ?? null : null;
  const previous = useRef<FilterView | null>(null);
  const view = useMemo((): FilterView | null => {
    if (!opened || !stored || !active) {
      previous.current = null;
      return null;
    }
    const key = filterViewKey(stored.id, active);
    const made = filterArrangement(opened, stored, active, { locks });
    const { result, kept } = keepSelection(previous.current, key, made, selected, fullTree);
    const next = { key, result, kept, query: snippetQuery(active) };
    previous.current = next;
    return next;
  }, [opened, stored, active, locks, selected, fullTree]);

  const tree = useMemo(() => (view ? visibleTree(fullTree, view.result) : fullTree), [fullTree, view]);
  // The fragments it matched, and every fragment in the arrangement (one it doesn't hold always "matches").
  const matching = useMemo(() => {
    const out = new Set<string>(), all = new Set<string>();
    if (!view) return { out, all };
    const walk = (nodes: TreeNode[]) => nodes.forEach((n) => {
      if (n.fragmentId) {
        all.add(n.fragmentId);
        if (view.result.matches.has(n.id) || view.kept === n.id) out.add(n.fragmentId);
      }
      walk(n.children);
    });
    walk(fullTree);
    return { out, all };
  }, [fullTree, view]);
  // `filteredManuscript`: the manuscript filtered, whichever arrangement shows (Publish ▸ only the filtered scenes).
  const manuscript = useMemo(() => (opened && active ? filteredManuscript(opened, active, locks) : null), [opened, active, locks]);
  const node = useMemo(() => {
    if (!selected) return null;
    const shown = find(tree, selected);
    if (shown || !view) return shown;
    // Selected but not shown (chosen before the filter): it stays the page's item, with only what the filter lets through.
    const whole = find(fullTree, selected);
    return whole ? visibleNode(whole, view.result) : null;
  }, [tree, fullTree, selected, view]);

  // A tag chip or a value anywhere filters the contents (`requestFilter`).
  const latest = useRef({ state, isSaved });
  latest.current = { state, isSaved };
  useEffect(() => onFilterRequest((r) => {
    const { state: s, isSaved: kept } = latest.current;
    set(filterBy(s, r.rules, r.adding, kept));
  }), [raw]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!opened || !arrangement) return { filter: null, tree: fullTree, node: selected ? find(fullTree, selected) : null };
  const nodeTitle = (id: string) => find(fullTree, id)?.title || null;
  const filter: BinderFilter = {
    state,
    active,
    view,
    saved,
    edited: isFilterEdited(settled, saved),
    set,
    addFacet: (rule) => set(addFacet(state, rule, isSaved)),
    replaceFacet: (rule) => set(replaceFacet(state, rule, isSaved)),
    removeFacet: (id) => set(removeFacet(state, id, isSaved)),
    filterBy: (rules, adding = false) => set(filterBy(state, rules, adding, isSaved)),
    load: (search: SavedSearch) => {
      set(loadFilter(state, search));
      if (search.scope === 'manuscript' && arrangement.role !== 'manuscript' && arrangement.role !== 'alternate') o.show('manuscript');
      else if (search.scope === 'research' && arrangement.role !== 'reference') o.show('reference');
    },
    focus: (nodeId) => {
      set(focus(state, nodeId, isSaved));
      announce(nodeId ? `Showing only “${nodeTitle(nodeId) ?? 'Untitled'}”` : `Showing all of ${arrangement.name}`);
    },
    clear: () => set(clearFilter(state)),
    matchesFragment: (fragmentId) => !view || !matching.all.has(fragmentId) || matching.out.has(fragmentId),
    filteredManuscript: manuscript,
    isContext: (nodeId) => !!view && (view.result.context.has(nodeId) || view.kept === nodeId),
    snippet: (fragmentId) => (view?.query ? fragmentSnippet(opened, fragmentId, view.query) : null),
    step: (forward) => {
      const target = stepTarget(fullTree, view, selected, forward);
      if (target) o.select(target);
      else announce(forward ? 'No document after this one' : 'No document before this one');
    },
  };
  return { filter, tree, node };
}

function find(nodes: TreeNode[], id: string): TreeNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const inner = find(n.children, id);
    if (inner) return inner;
  }
  return null;
}
